/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useSelector} from 'react-redux';
import {User} from '@ion/core/src/types/entities';
import {dehydrateCurrentUser, useCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
import {loggedInUserSelector} from '../../../store/selectors/login';

/**
 * Provides a utility which allows for referencing the current user imperatively
 * from either the React Query Cache, or the Redux Store. This utility essentially
 * serves to ensure backwards compatibility while migrating from Redux to React
 * Query.
 *
 * ```typescript
 * import {getCurrentUser} from '@ion/applications/src/pages/admin/users';
 *
 * // returns the current user from either React Query, or the Redux Store ...
 * const user = getCurrentUser();
 *
 * ```
 */
export const getCurrentUser = (getState: Function): User => {
  return dehydrateCurrentUser() || getState().login?.user;
};

/**
 * The useAuthenticatedUser hook provides a convenience hook for referencing the
 * current user either via the persisted current user in the React Query cache
 * (default) or the persisted User in the Redux Store.
 *
 * This hook is essentially intended to abstract away the intermediary migration
 * implementation details from Redux to React Query.
 *
 * ```typescript
 * import {useAuthenticatedUser} from '@ion/applications/src/pages/admin/users/useAuthenticatedUser';
 *
 * // returns the current user from either React Query, or the Redux Store ...
 * const user = useAuthenticatedUser();
 *
 * ```
 */
export const useAuthenticatedUser = (): User => {
  const queriedCurrentUser = useCurrentUser()?.currentUser;
  const storeCurrentUser   = useSelector(loggedInUserSelector);
  if (Object.keys(queriedCurrentUser)?.length) {
    return queriedCurrentUser;
  }
  return storeCurrentUser;
};
export default useAuthenticatedUser;
